import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import GameLayout from '../layout/GameLayout';
import followService from '../../services/api/followService';
import '../../styles/features/followList.css';

const PAGE_SIZE = 20;

function FollowList({ token, onLogout }) {
  const { userId } = useParams();
  const navigate   = useNavigate();
  const location   = useLocation();

  // Derive list type from the URL path segment
  const type  = location.pathname.includes('/following') ? 'following' : 'followers';
  const title = type === 'followers' ? 'Followers' : 'Following';

  const [users,   setUsers]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  // Pages of PAGE_SIZE with a "Load more" button (MS-B18: only the first 20 ever showed)
  const [page,    setPage]    = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback((pageNum) => (
    type === 'followers'
      ? followService.getFollowers(userId, { page: pageNum, limit: PAGE_SIZE })
      : followService.getFollowing(userId, { page: pageNum, limit: PAGE_SIZE })
  ), [userId, type]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await fetchPage(1);
      const list = res.data?.users ?? [];
      setUsers(list);
      setPage(1);
      setHasMore(list.length === PAGE_SIZE);
    } catch {
      setError('Failed to load list. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = async () => {
    try {
      setLoadingMore(true);
      const next = page + 1;
      const res = await fetchPage(next);
      const list = res.data?.users ?? [];
      setUsers((prev) => {
        const seen = new Set(prev.map((u) => String(u.id)));
        return [...prev, ...list.filter((u) => !seen.has(String(u.id)))];
      });
      setPage(next);
      setHasMore(list.length === PAGE_SIZE);
    } catch {
      setError('Failed to load more. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="fl">
        <button className="fl-back" onClick={() => navigate(`/players/${userId}`)}>
          Back
        </button>

        <h2 className="fl-title">{title}</h2>

        {loading && <p className="fl-state">Loading...</p>}

        {!loading && error && (
          <p className="fl-state fl-state--error">{error}</p>
        )}

        {!loading && !error && users.length === 0 && (
          <p className="fl-state">No {title.toLowerCase()} yet.</p>
        )}

        {!loading && !error && users.length > 0 && (
          <ul className="fl-list">
            {users.map((u) => (
              <li
                key={u.id}
                className="fl-item"
                onClick={() => navigate(`/players/${u.uuid || u.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className="fl-avatar">
                  {u.avatar_url
                    ? <img src={u.avatar_url} alt={`${u.first_name} avatar`} className="fl-avatar-img" />
                    : <span className="fl-avatar-placeholder">{u.first_name?.[0]?.toUpperCase() ?? '?'}</span>
                  }
                </div>
                <div className="fl-info">
                  <span className="fl-name">{u.first_name} {u.last_name}</span>
                  <span className="fl-username">@{u.username}</span>
                </div>
              </li>
            ))}
          </ul>
        )}

        {!loading && !error && hasMore && (
          <button className="fl-back" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Loading...' : 'Load more'}
          </button>
        )}
      </div>
    </GameLayout>
  );
}

export default FollowList;
